/**
 * Google Calendar API Client
 *
 * Provides methods for creating, updating, and deleting events in Google Calendar.
 * Uses the same OAuth token as Google Drive.
 *
 * One-way sync: NIDOnote events → Google Calendar
 * Changes in Google Calendar do NOT sync back to NIDOnote
 */

export interface GoogleCalendarEvent {
  summary: string; // Event title
  description?: string; // Event description
  start: {
    dateTime?: string; // ISO 8601 format (for timed events)
    date?: string; // YYYY-MM-DD (for all-day events)
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  location?: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: 'email' | 'popup';
      minutes: number;
    }>;
  };
}

interface GoogleCalendarEventResponse {
  id: string;
  summary: string;
  description?: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  location?: string;
  status: string;
  htmlLink: string;
}

/**
 * Google Calendar API Client
 */
class GoogleCalendarClient {
  private accessToken: string | null = null;
  private readonly baseUrl = 'https://www.googleapis.com/calendar/v3';

  /**
   * Initialize the client with an access token
   */
  initialize(accessToken: string): void {
    this.accessToken = accessToken;
  }

  /**
   * Check if client is initialized
   */
  private ensureInitialized(): void {
    if (!this.accessToken) {
      throw new Error('Google Calendar client not initialized. Call initialize() first.');
    }
  }

  /**
   * Make a request to Google Calendar API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    this.ensureInitialized();

    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails;
      try {
        errorDetails = JSON.parse(errorText);
      } catch {
        errorDetails = errorText;
      }

      // Log detailed error information for debugging
      console.error('[Google Calendar] API Error:', {
        status: response.status,
        statusText: response.statusText,
        url,
        method: options.method || 'GET',
        errorDetails,
      });

      throw new Error(
        `Google Calendar API error (${response.status}): ${errorText}`
      );
    }

    // Handle empty responses (e.g., DELETE returns 204 No Content)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Create an event in Google Calendar
   *
   * @param event - Event data
   * @param calendarId - Calendar ID (default: 'primary')
   * @returns Google Calendar event ID
   */
  async createEvent(
    event: GoogleCalendarEvent,
    calendarId: string = 'primary'
  ): Promise<string> {
    // Log the event payload for debugging
    console.log('[Google Calendar] Creating event with payload:', JSON.stringify(event, null, 2));

    const response = await this.request<GoogleCalendarEventResponse>(
      `/calendars/${calendarId}/events`,
      {
        method: 'POST',
        body: JSON.stringify(event),
      }
    );

    console.log('[Google Calendar] Event created successfully:', response.id);
    return response.id;
  }

  /**
   * Update an existing event in Google Calendar
   *
   * @param eventId - Google Calendar event ID
   * @param event - Updated event data
   * @param calendarId - Calendar ID (default: 'primary')
   */
  async updateEvent(
    eventId: string,
    event: GoogleCalendarEvent,
    calendarId: string = 'primary'
  ): Promise<void> {
    await this.request<GoogleCalendarEventResponse>(
      `/calendars/${calendarId}/events/${eventId}`,
      {
        method: 'PUT',
        body: JSON.stringify(event),
      }
    );
  }

  /**
   * Delete an event from Google Calendar
   *
   * @param eventId - Google Calendar event ID
   * @param calendarId - Calendar ID (default: 'primary')
   */
  async deleteEvent(
    eventId: string,
    calendarId: string = 'primary'
  ): Promise<void> {
    try {
      await this.request(
        `/calendars/${calendarId}/events/${eventId}`,
        {
          method: 'DELETE',
        }
      );
    } catch (error: any) {
      // If event already deleted (404), consider it success
      if (error?.message?.includes('404')) {
        console.log(`Event already deleted from Google Calendar: ${eventId}`);
        return;
      }
      throw error;
    }
  }

  /**
   * Get an event from Google Calendar
   *
   * @param eventId - Google Calendar event ID
   * @param calendarId - Calendar ID (default: 'primary')
   */
  async getEvent(
    eventId: string,
    calendarId: string = 'primary'
  ): Promise<GoogleCalendarEventResponse | null> {
    try {
      return await this.request<GoogleCalendarEventResponse>(
        `/calendars/${calendarId}/events/${eventId}`
      );
    } catch (error: any) {
      if (error?.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * ===== CALENDAR MANAGEMENT METHODS (for Shared Spaces) =====
   */

  /**
   * Create a new Google Calendar
   *
   * @param summary - Calendar name (e.g., "Family Tasks")
   * @param description - Calendar description
   * @param timeZone - Calendar timezone (defaults to user's timezone)
   * @param color - Calendar color (optional)
   * @returns Calendar ID
   */
  async createCalendar(
    summary: string,
    description?: string,
    timeZone?: string,
    color?: string
  ): Promise<{ id: string; summary: string; description?: string }> {
    const calendarData: any = {
      summary,
      description,
      timeZone: timeZone || this.getUserTimezone(),
    };

    console.log('[Google Calendar] Creating calendar with payload:', JSON.stringify(calendarData, null, 2));

    const response = await this.request<any>(
      '/calendars',
      {
        method: 'POST',
        body: JSON.stringify(calendarData),
      }
    );

    console.log('[Google Calendar] Calendar created successfully:', response.id);

    // Set calendar color if provided
    if (color && response.id) {
      await this.setCalendarColor(response.id, color);
    }

    return {
      id: response.id,
      summary: response.summary,
      description: response.description,
    };
  }

  /**
   * Update an existing calendar
   *
   * @param calendarId - Calendar ID
   * @param updates - Calendar updates (summary, description, timeZone)
   */
  async updateCalendar(
    calendarId: string,
    updates: {
      summary?: string;
      description?: string;
      timeZone?: string;
    }
  ): Promise<void> {
    console.log('[Google Calendar] Updating calendar:', calendarId);

    await this.request(
      `/calendars/${calendarId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );

    console.log('[Google Calendar] Calendar updated successfully');
  }

  /**
   * Delete a calendar
   *
   * @param calendarId - Calendar ID
   */
  async deleteCalendar(calendarId: string): Promise<void> {
    try {
      console.log('[Google Calendar] Deleting calendar:', calendarId);

      await this.request(
        `/calendars/${calendarId}`,
        {
          method: 'DELETE',
        }
      );

      console.log('[Google Calendar] Calendar deleted successfully');
    } catch (error: any) {
      // If calendar already deleted (404 or 410), consider it success
      if (error?.message?.includes('404') || error?.message?.includes('410')) {
        console.log(`Calendar already deleted: ${calendarId}`);
        return;
      }
      throw error;
    }
  }

  /**
   * Set calendar color in user's calendar list
   *
   * @param calendarId - Calendar ID
   * @param hexColor - Hex color (e.g., "#4A90E2")
   */
  private async setCalendarColor(calendarId: string, hexColor: string): Promise<void> {
    try {
      // Google Calendar uses predefined colorIds (1-24)
      // We'll map our hex colors to the closest colorId
      const colorId = this.hexToCalendarColorId(hexColor);

      await this.request(
        `/users/me/calendarList/${encodeURIComponent(calendarId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            colorId,
          }),
        }
      );

      console.log('[Google Calendar] Calendar color set to:', colorId);
    } catch (error) {
      console.warn('[Google Calendar] Failed to set calendar color:', error);
      // Non-critical error, don't throw
    }
  }

  /**
   * Map hex color to Google Calendar colorId (1-24)
   */
  private hexToCalendarColorId(hexColor: string): string {
    // Google Calendar color mapping (closest matches to our palette)
    const colorMap: Record<string, string> = {
      '#4A90E2': '9',  // Sky Blue → Blue
      '#F5A623': '6',  // Orange → Orange
      '#7ED321': '10', // Green → Green
      '#D0021B': '11', // Red → Red
      '#9013FE': '3',  // Purple → Purple
      '#50E3C2': '7',  // Teal → Cyan
      '#F8E71C': '5',  // Yellow → Yellow
      '#BD10E0': '4',  // Magenta → Grape
    };

    return colorMap[hexColor] || '9'; // Default to blue
  }

  /**
   * ===== ACCESS CONTROL LIST (ACL) METHODS =====
   */

  /**
   * Share a calendar with a user
   *
   * @param calendarId - Calendar ID
   * @param email - User's email address
   * @param role - Access role ('owner', 'writer', 'reader')
   * @returns ACL rule ID
   */
  async shareCalendar(
    calendarId: string,
    email: string,
    role: 'owner' | 'writer' | 'reader'
  ): Promise<string> {
    console.log(`[Google Calendar] Sharing calendar ${calendarId} with ${email} as ${role}`);

    // Check if ACL already exists for this email
    const existingRule = await this.getACLRuleForEmail(calendarId, email);
    if (existingRule) {
      console.log('[Google Calendar] ACL already exists, updating role');
      await this.updateCalendarAccess(calendarId, existingRule.id, role);
      return existingRule.id;
    }

    const aclRule = {
      scope: {
        type: 'user',
        value: email,
      },
      role,
    };

    const response = await this.request<any>(
      `/calendars/${encodeURIComponent(calendarId)}/acl`,
      {
        method: 'POST',
        body: JSON.stringify(aclRule),
      }
    );

    console.log('[Google Calendar] Calendar shared successfully, ACL rule ID:', response.id);
    return response.id;
  }

  /**
   * Revoke calendar access for a user
   *
   * @param calendarId - Calendar ID
   * @param ruleId - ACL rule ID
   */
  async revokeCalendarAccess(calendarId: string, ruleId: string): Promise<void> {
    try {
      console.log(`[Google Calendar] Revoking calendar access, rule ID: ${ruleId}`);

      await this.request(
        `/calendars/${encodeURIComponent(calendarId)}/acl/${ruleId}`,
        {
          method: 'DELETE',
        }
      );

      console.log('[Google Calendar] Access revoked successfully');
    } catch (error: any) {
      // If ACL already removed (404), consider it success
      if (error?.message?.includes('404')) {
        console.log(`ACL rule already removed: ${ruleId}`);
        return;
      }
      throw error;
    }
  }

  /**
   * Update calendar access role for a user
   *
   * @param calendarId - Calendar ID
   * @param ruleId - ACL rule ID
   * @param newRole - New access role
   */
  async updateCalendarAccess(
    calendarId: string,
    ruleId: string,
    newRole: 'owner' | 'writer' | 'reader'
  ): Promise<void> {
    console.log(`[Google Calendar] Updating calendar access, rule ID: ${ruleId}, new role: ${newRole}`);

    await this.request(
      `/calendars/${encodeURIComponent(calendarId)}/acl/${ruleId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      }
    );

    console.log('[Google Calendar] Access updated successfully');
  }

  /**
   * List all ACL rules for a calendar
   *
   * @param calendarId - Calendar ID
   * @returns Array of ACL rules
   */
  async listCalendarACL(calendarId: string): Promise<Array<{
    id: string;
    role: string;
    scope: { type: string; value?: string };
  }>> {
    const response = await this.request<any>(
      `/calendars/${encodeURIComponent(calendarId)}/acl`
    );

    return response.items || [];
  }

  /**
   * Get ACL rule for a specific email address
   *
   * @param calendarId - Calendar ID
   * @param email - User's email address
   * @returns ACL rule or null if not found
   */
  async getACLRuleForEmail(
    calendarId: string,
    email: string
  ): Promise<{ id: string; role: string } | null> {
    try {
      const rules = await this.listCalendarACL(calendarId);
      const rule = rules.find(
        (r) => r.scope.type === 'user' && r.scope.value === email
      );

      return rule ? { id: rule.id, role: rule.role } : null;
    } catch (error) {
      console.warn('[Google Calendar] Failed to get ACL rule for email:', error);
      return null;
    }
  }

  /**
   * Convert NIDOnote location string to Google Calendar location
   */
  private formatLocation(location?: string): string | undefined {
    if (!location) return undefined;
    return location;
  }

  /**
   * Get user's timezone (defaults to America/New_York for now)
   * TODO: Allow user to configure timezone in settings
   */
  private getUserTimezone(): string {
    // Try to get browser timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/New_York';
    }
  }

  /**
   * Helper to convert NIDOnote event to Google Calendar format
   */
  createEventFromNIDOnote(event: {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    isAllDay: boolean;
    location?: string;
  }): GoogleCalendarEvent {
    const timezone = this.getUserTimezone();

    // Format dateTime to RFC3339 (must include seconds)
    const formatDateTime = (dateStr: string): string => {
      // If already has seconds (HH:MM:SS), use as-is
      if (dateStr.match(/T\d{2}:\d{2}:\d{2}/)) {
        return dateStr;
      }
      // If only has HH:MM, add :00 for seconds
      if (dateStr.match(/T\d{2}:\d{2}$/)) {
        return dateStr + ':00';
      }
      // Default: assume it needs seconds
      return dateStr + ':00';
    };

    const gcalEvent: GoogleCalendarEvent = {
      summary: event.title,
      description: event.description || '',
      start: event.isAllDay
        ? { date: event.startTime.split('T')[0] }
        : { dateTime: formatDateTime(event.startTime), timeZone: timezone },
      end: event.isAllDay
        ? { date: event.endTime.split('T')[0] }
        : { dateTime: formatDateTime(event.endTime), timeZone: timezone },
    };

    if (event.location) {
      gcalEvent.location = this.formatLocation(event.location);
    }

    // Use default reminders
    gcalEvent.reminders = {
      useDefault: true,
    };

    return gcalEvent;
  }
}

// Export singleton instance
export const calendarClient = new GoogleCalendarClient();
